import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GuidedCaseLayout, type ChecklistItem, type GuidedStep } from "@/components/radnici/GuidedCaseLayout";
import { InvoiceNavigation } from "@/components/nabava/InvoiceNavigation";
import { CaseCard } from "@/components/radnici/ProcessCaseUI";
import { errorMessage } from "@/lib/api";
import { localToday } from "@/lib/permitProcesses";
import { hasProcurement } from "@/lib/procurement";
import { costCenter, invoiceMoney } from "@/lib/receivedInvoices";
import {
  expenseNet,
  expenseReceipts,
  receiptFieldsReason,
  type Expense,
  type ExpenseInput,
  type ExpenseOptions,
} from "@/lib/expenseReceipts";
import "@/components/nabava/ReceivedInvoices.css";

const emptyForm = (): ExpenseInput => ({
  trgovina: "",
  datum: "",
  iznos: "",
  pdv: "",
  gradiliste_id: "",
  platio: "radnik",
  povrat_korisnik_id: null,
  opis: "",
  dokument_id: "",
});

export function ExpenseWithoutOrderPage() {
  const { user } = useAuth(),
    allowed = hasProcurement(user, "create");
  const [form, setForm] = useState(emptyForm),
    [saved, setSaved] = useState<Expense | null>(null);
  const [options, setOptions] = useState<{ day: string; data: ExpenseOptions } | null>(null);
  const [optionsError, setOptionsError] = useState(""),
    [reload, setReload] = useState(0);
  const [reading, setReading] = useState(false),
    [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState(""),
    [error, setError] = useState(""),
    [preview, setPreview] = useState("");
  const [readDone, setReadDone] = useState(false);
  const controller = useRef<AbortController | null>(null),
    sending = useRef(false);
  const day = /^\d{4}-\d{2}-\d{2}$/.test(form.datum) ? form.datum : localToday();
  useEffect(() => {
    if (!allowed) return;
    const abort = new AbortController();
    expenseReceipts
      .options(day, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) {
          setOptions({ day, data });
          setOptionsError("");
        }
      })
      .catch((cause) => {
        if (!abort.signal.aborted) {
          setOptions(null);
          setOptionsError(errorMessage(cause, "Gradilišta i konta nisu učitani."));
        }
      });
    return () => abort.abort();
  }, [allowed, day, reload]);
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const choices = options?.day === day ? options.data : null;
  const site = choices?.sites.find((site) => site.id === form.gradiliste_id);
  const recipient = choices?.recipients.find((person) => person.id === form.povrat_korisnik_id);
  const fieldsReason = receiptFieldsReason(form);
  const reason = saving
    ? "Slanje je u tijeku."
    : reading
      ? "Pričekajte: čitam račun…"
      : saved
        ? "Trošak je već poslan Nikolini."
        : optionsError ||
          (!choices
            ? "Pričekajte učitavanje gradilišta i konta."
            : !site
              ? "Odaberite gradilište na kojem smijete naručivati."
              : !form.dokument_id
                ? "Priložite fotografiju računa."
                : fieldsReason ||
                  (form.platio === "radnik" && !recipient
                    ? "Odaberite kome treba vratiti novac."
                    : !form.opis.trim()
                      ? "Upišite što je kupljeno i zašto."
                      : ""));
  const change = <K extends keyof ExpenseInput>(key: K, value: ExpenseInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const focus = (id: string) => document.getElementById(id)?.focus();
  async function readPhoto(file: File | undefined) {
    if (!file || !site || saving || saved) return;
    controller.current?.abort();
    controller.current = new AbortController();
    const signal = controller.current.signal;
    setReading(false);
    setError("");
    setWarning("");
    setReadDone(false);
    setForm((current) => ({ ...current, dokument_id: "", trgovina: "", datum: "", iznos: "", pdv: "" }));
    if (!["image/jpeg", "image/png"].includes(file.type) || file.size > 15 * 1024 * 1024) {
      setError("Priložite JPEG ili PNG fotografiju računa do 15 MB.");
      setPreview("");
      return;
    }
    setPreview(typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "");
    setReading(true);
    try {
      const result = await expenseReceipts.read(site.id, file, signal);
      if (signal.aborted) return;
      setForm((current) => ({
        ...current,
        dokument_id: result.dokument_id,
        trgovina: result.fields.trgovina || "",
        datum: result.fields.datum || "",
        iznos: result.fields.iznos || "",
        pdv: result.fields.pdv || "",
      }));
      setWarning(result.warning || "");
      setReadDone(true);
    } catch (cause) {
      if (!signal.aborted) setError(errorMessage(cause, "Fotografija nije spremljena. Ponovno je priložite."));
    } finally {
      if (!signal.aborted) setReading(false);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (reason || sending.current || !allowed) return;
    sending.current = true;
    setSaving(true);
    setError("");
    try {
      setSaved(await expenseReceipts.send(form));
    } catch (cause) {
      setError(errorMessage(cause, "Trošak nije poslan. Pokušajte ponovno."));
    } finally {
      sending.current = false;
      setSaving(false);
    }
  }
  if (!allowed)
    return (
      <div className="invoice-screen space-y-4 py-4">
        <InvoiceNavigation />
        <h1 className="text-2xl font-bold">Trošak bez narudžbe</h1>
        <p role="alert">Nemate ovlast naručivanja za unos troška bez narudžbe.</p>
      </div>
    );
  const net = saved?.trosak_iznos ?? expenseNet(form.iznos, form.pdv);
  const edit = (id: string, text: string) =>
    !saved ? (
      <Button variant="secondary" onClick={() => focus(id)}>
        {text}
      </Button>
    ) : undefined;
  const checklist: ChecklistItem[] = [
    {
      id: "photo",
      label: "Fotografija računa sačuvana",
      ready: !!form.dokument_id,
      actions: edit("expense-photo", "Priloži fotografiju"),
    },
    {
      id: "fields",
      label: "Trgovina, datum, bruto iznos i PDV",
      ready: !fieldsReason,
      detail: fieldsReason || "Provjerite podatke prema fotografiji.",
      actions: edit("expense-shop", "Provjeri podatke"),
    },
    {
      id: "site",
      label: "Gradilište i mjesto troška",
      ready: !!site,
      detail: site ? `${site.naziv} · mjesto troška ${costCenter(site.mjesto_troska)}` : "Odaberite gradilište.",
      actions: edit("expense-site", "Odaberi gradilište"),
    },
    {
      id: "refund",
      label: form.platio === "radnik" ? "Primatelj povrata odabran" : "Službena kartica, povrat nije potreban",
      ready: form.platio === "kartica" || !!recipient,
      actions: form.platio === "radnik" ? edit("expense-recipient", "Odaberi primatelja") : undefined,
    },
    {
      id: "description",
      label: "Opis kupnje upisan",
      ready: !!form.opis.trim(),
      actions: edit("expense-description", "Dopuni opis"),
    },
  ];
  const steps: GuidedStep[] = [
    { id: "photo", name: "Fotografiraj račun", state: form.dokument_id ? "completed" : "current" },
    {
      id: "review",
      name: "Provjeri i dopuni podatke",
      description: "Nakon čitanja računa provjerite sva polja prema fotografiji.",
      state: saved || !reason ? "completed" : form.dokument_id ? "current" : "future",
    },
    {
      id: "send",
      name: "Pošalji Nikolini",
      state: saved ? "completed" : !reason ? "current" : "future",
      description: "Slanje je dostupno kad su svi potrebni podaci dopunjeni.",
    },
    {
      id: "review-nikolina",
      name: "Nikolinina obrada",
      state: "future",
      description: "Nikolina pregledava trošak i povrat. Plaće se ne diraju.",
    },
  ];
  const disabled = reading || saving || !!saved;
  return (
    <div className="invoice-screen space-y-4 py-4">
      <InvoiceNavigation />
      {error && <p role="alert">{error}</p>}
      {saved && (
        <p role="status" className="rounded-xl bg-green-50 p-4 text-green-950">
          Poslano Nikolini.{" "}
          {saved.stanje_povrata === "treba_povrat"
            ? "Povrat se obrađuje zasebno, izvan plaće."
            : "Plaćeno službenom karticom; povrat radniku nije potreban."}
        </p>
      )}
      <GuidedCaseLayout
        number={saved ? "Poslan trošak" : "Novi trošak"}
        kind="Bez narudžbe"
        person="Trošak bez narudžbe"
        owner="Nikolina"
        summary={
          saved
            ? "Trošak poslan Nikolini"
            : reading
              ? "Čitam račun…"
              : readDone
                ? "Provjerite pročitane podatke i pošaljite Nikolini"
                : "Priložite račun i dopunite podatke"
        }
        tone={saved ? "good" : "warn"}
        signals={[]}
        steps={steps}
        checklist={checklist}
        stepOrder="sequential"
        omitMissingAttribution
        currentTitle={saved ? undefined : "Sljedeći korak"}
        instructions={
          reading
            ? "Čitam račun… Pričekajte završetak čitanja."
            : "Provjerite trgovinu, datum, bruto iznos i PDV. Svako polje možete ispraviti ručno."
        }
        primaryAction={
          !saved && (
            <>
              <Button
                type="submit"
                form="expense-form"
                disabled={!!reason}
                aria-describedby={reason ? "expense-send-reason" : undefined}
              >
                Pošalji Nikolini
              </Button>
              {reason && (
                <p id="expense-send-reason" className="mt-2 text-sm">
                  {reason}
                </p>
              )}
            </>
          )
        }
        currentContent={
          <form id="expense-form" className="space-y-4" onSubmit={(event) => void submit(event)}>
            {optionsError && (
              <>
                <p role="alert">{optionsError}</p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setOptionsError("");
                    setReload((value) => value + 1);
                  }}
                >
                  Ponovno učitaj gradilišta i konta
                </Button>
              </>
            )}
            <fieldset disabled={disabled} className="space-y-4 min-w-0">
              <label htmlFor="expense-site">
                Gradilište
                <select
                  id="expense-site"
                  value={form.gradiliste_id}
                  onChange={(event) => change("gradiliste_id", event.target.value)}
                >
                  <option value="">Odaberite gradilište</option>
                  {choices?.sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.naziv}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="expense-photo">
                Fotka računa iz trgovine
                <input
                  id="expense-photo"
                  type="file"
                  accept="image/jpeg,image/png"
                  capture="environment"
                  disabled={!site}
                  aria-describedby={!site ? "expense-photo-reason" : undefined}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    void readPhoto(file);
                  }}
                />
              </label>
              {!site && (
                <p id="expense-photo-reason" className="text-sm">
                  Najprije odaberite gradilište za unos računa.
                </p>
              )}
              {reading && (
                <p role="status" className="rounded-lg bg-blue-50 p-3 text-blue-950">
                  Čitam račun…
                </p>
              )}
              {warning && (
                <p role="status" className="rounded-lg bg-amber-50 p-3 text-amber-950">
                  {warning}
                </p>
              )}
              {readDone && !warning && (
                <p role="status" className="rounded-lg bg-blue-50 p-3 text-blue-950">
                  AI je pročitao račun. Provjerite podatke prema fotografiji.
                </p>
              )}
              <label htmlFor="expense-shop">
                Trgovina
                <input
                  id="expense-shop"
                  maxLength={200}
                  value={form.trgovina}
                  onChange={(event) => change("trgovina", event.target.value)}
                />
              </label>
              <label htmlFor="expense-date">
                Datum računa
                <input
                  id="expense-date"
                  type="date"
                  min="0001-01-01"
                  max="9999-12-31"
                  value={form.datum}
                  onChange={(event) => change("datum", event.target.value)}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="expense-gross">
                  Bruto iznos (€)
                  <input
                    id="expense-gross"
                    inputMode="decimal"
                    value={form.iznos}
                    onChange={(event) => change("iznos", event.target.value)}
                  />
                </label>
                <label htmlFor="expense-vat">
                  PDV (€)
                  <input
                    id="expense-vat"
                    inputMode="decimal"
                    value={form.pdv}
                    onChange={(event) => change("pdv", event.target.value)}
                  />
                </label>
              </div>
              <fieldset>
                <legend className="text-sm mb-2">Tko je platio</legend>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    aria-pressed={form.platio === "radnik"}
                    onClick={() => change("platio", "radnik")}
                  >
                    Radnik, svojim novcem
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    aria-pressed={form.platio === "kartica"}
                    onClick={() => {
                      change("platio", "kartica");
                      change("povrat_korisnik_id", null);
                    }}
                  >
                    Službena kartica
                  </Button>
                </div>
              </fieldset>
              {form.platio === "radnik" && (
                <label htmlFor="expense-recipient">
                  Kome treba vratiti novac
                  <select
                    id="expense-recipient"
                    value={form.povrat_korisnik_id || ""}
                    onChange={(event) => change("povrat_korisnik_id", event.target.value || null)}
                  >
                    <option value="">Odaberite primatelja</option>
                    {choices?.recipients.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.naziv}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label htmlFor="expense-description">
                Što je kupljeno i zašto
                <textarea
                  id="expense-description"
                  maxLength={2000}
                  value={form.opis}
                  onChange={(event) => change("opis", event.target.value)}
                />
              </label>
            </fieldset>
          </form>
        }
        metadata={
          <CaseCard title="Kod Nikoline">
            <dl className="invoice-facts">
              <dt>Obveza (bruto)</dt>
              <dd>
                {choices?.konto_obveze || "—"} · {invoiceMoney(saved?.iznos ?? expenseNet(form.iznos, "0") ?? null)}
              </dd>
              <dt>Trošak bez PDV-a</dt>
              <dd>
                {choices?.konto_troska || "—"} · {costCenter(site?.mjesto_troska ?? null)} · {invoiceMoney(net)}
              </dd>
              <dt>Gradilište</dt>
              <dd>{site?.naziv || "—"}</dd>
              <dt>Mjesto troška</dt>
              <dd>{costCenter(site?.mjesto_troska ?? null)}</dd>
              <dt>Povrat</dt>
              <dd>
                {form.platio === "radnik"
                  ? recipient
                    ? `${recipient.naziv}, ${invoiceMoney(saved?.iznos ?? expenseNet(form.iznos, "0") ?? null)}, zasebna isplata`
                    : "Odaberite primatelja"
                  : "Službena kartica — nema povrata radniku"}
              </dd>
            </dl>
            <p className="text-sm">Plaće se ne diraju. Fotografija ostaje uz zapis.</p>
            {preview && <img src={preview} alt="Priložena fotografija računa" className="max-w-full rounded-lg" />}
          </CaseCard>
        }
        metadataOpen
        diary={
          <ul className="space-y-2 text-sm">
            {form.dokument_id && <li>Fotografija računa sačuvana.</li>}
            {readDone && (
              <li>
                {warning
                  ? "Podaci računa dostupni za ručni unos i ispravak."
                  : "Račun pročitan; podatke provjerava unositelj."}
              </li>
            )}
            {saved && (
              <li>
                Trošak poslan Nikolini.{" "}
                {saved.stanje_povrata === "treba_povrat"
                  ? "Zabilježen potreban povrat izvan plaće."
                  : "Povrat radniku nije potreban."}
              </li>
            )}
          </ul>
        }
      />
      {saved && (
        <Button
          onClick={() => {
            setForm(emptyForm());
            setSaved(null);
            setPreview("");
            setReadDone(false);
            setWarning("");
            setError("");
          }}
        >
          Unesi novi trošak
        </Button>
      )}
    </div>
  );
}
